import { Box, Flex, useColorModeValue } from '@chakra-ui/react';
import {
  Row,
  RowSelectionState,
  Table,
  flexRender,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Fragment, memo, useCallback, useRef } from 'react';

import { round } from 'lodash';
import ColumnManagementModal from '../../../common/ColumnManagementModal';
import {
  EventType,
  OrderColumnDeepFragment,
  OrderColumnPatch,
} from '../../../../gql/graphql';
import { useProjectHistory } from '../../project-history/hooks/useProjectHistory';
import { useDiffing } from '../../project-history/hooks/useDiffing';
import { DiffBadge } from '../../project-history/DiffBadge';
import { OrderEntryTableRow } from './columns';
import AggregatedOrderTableHeader from './header';

interface AggregatedOrderTableProps {
  table: Table<OrderEntryTableRow>;
  showColumnManagementModal: boolean;
  orderColumns: OrderColumnDeepFragment[];
  thisOrderId: string;
  onColumnManagementModalClose: () => void;
  handleColumnManagementSubmit: (
    orders: Array<OrderColumnPatch>
  ) => Promise<void>;
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  renamingColumnId: string | null;
  onRename: (columnId: string | null, newName?: string) => void;
  columnNameMapping?: Record<string, { id: string; newName: string }>;
}

export function getAggEntryDiffPath(
  rows: Row<OrderEntryTableRow>[],
  rowId: string
): string {
  const keyFragments: string[] = [];
  let currentParentId: string | undefined = rowId;
  while (currentParentId) {
    const found = rows.find((cRow) => cRow.id === currentParentId);
    if (!found) break;
    currentParentId = found.parentId;
    let subKey = found.original.name;
    // if !currentParentId then found.original.id
    // if !(parent in rows) then found.original.id
    // if (parent in row) && parent is grouped then found.original.id
    const parent = rows.find((cRow) => cRow.id === currentParentId);
    if (
      !currentParentId ||
      !parent ||
      (parent.original as any)?.['__isGrouped']
    ) {
      subKey = found.original.id;
    }
    if (currentParentId) {
      subKey = `subRows.${subKey}`;
    }
    keyFragments.push(subKey);
  }
  return keyFragments.reverse().join('.');
}

const AggregatedOrderTable = ({
  table,
  showColumnManagementModal,
  orderColumns,
  handleColumnManagementSubmit,
  onColumnManagementModalClose,
  setRowSelection,
  renamingColumnId,
  columnNameMapping,
  thisOrderId,
}: AggregatedOrderTableProps) => {
  const headerFontColor = useColorModeValue('gray.400', 'gray.500');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBackgroundColor = useColorModeValue('gray.50', 'gray.700');

  const containerRef = useRef<HTMLDivElement>(null);

  const { rows } = table.getRowModel();
  const { isHistoryDrawerOpen, diff } = useProjectHistory();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 30,
    getScrollElement: () => containerRef.current,
    getItemKey: (index: number) => {
      return rows[index].id;
    },
    overscan: 10,
  });

  const { getTextColor, getBackgroundColor } = useDiffing();
  const getEventType = useCallback(
    // eslint-disable-next-line complexity
    (row: Row<OrderEntryTableRow>) => {
      // Entry ID Is not important as this might exclude the IDs that are derived from the grouping magic
      const rowId = row.original.id;
      if (!isHistoryDrawerOpen || !diff) return;
      const collectionKey = `project.orders.${thisOrderId}.orderEntries`;

      const collectionDiff = diff[collectionKey];
      if (collectionDiff) {
        if (
          collectionDiff.new &&
          (!collectionDiff.old || collectionDiff.old === '[]') &&
          collectionDiff.new?.[rowId]
        ) {
          return EventType.Created;
        } else if (
          (!collectionDiff.new || collectionDiff.new === '[]') &&
          collectionDiff.old &&
          collectionDiff.old?.[rowId]
        ) {
          return EventType.Deleted;
        }
      }

      // This will be called for each row and differs from the diffing logic in that we start from the leaves going up
      // while in the diffing logic, we started from the root going down, recursing through each subrow
      // right now, we need to start from the leaves recursing through the parent but if we have no parent,
      // we must either be at the root or we are in a subtree not native to tbe table i.e., manufactured in order entries table

      // what are the different kinds of keys: 'unit:PCS', '0', '0.1.2'
      const entryPath = getAggEntryDiffPath(rows, row.id);
      const rowKey = `${collectionKey}.${entryPath}`;
      const rowDiff = diff[rowKey];
      if (rowDiff) {
        if (rowDiff.new && (!rowDiff.old || rowDiff.old === '[]')) {
          return EventType.Created;
        } else if ((!rowDiff.new || rowDiff.new === '[]') && rowDiff.old) {
          return EventType.Deleted;
        }
      }

      // If any of our ancestors has been marked as either having been created, we need to also be marked as having been created/destroyed
      // Check if any of the diff keys are a subkey of our current key and then do the new and old check dance
      // This seem to take care of the subrow collections as well
      for (const [key, value] of Object.entries(diff)) {
        if (!rowKey.includes(key) || rowKey === key) continue;
        if (value.new && (!value.old || value.old === '[]')) {
          return EventType.Created;
        } else if ((!value.new || value.new === '[]') && value.old) {
          return EventType.Deleted;
        }
      }

      // eslint-disable-next-line complexity
      const hasOtherChanges = Object.entries(diff).some(([key, value]) => {
        if (key.startsWith(rowKey)) {
          const oldValue = value.old;
          const newValue = value.new;
          if (typeof oldValue === 'number' && typeof newValue === 'number') {
            return round(oldValue, 2) !== round(newValue, 2);
          }
          if (
            key.includes('.subRows') &&
            key.includes('elementIdentifiersDbid')
          ) {
            return false;
          }
          // Ignore itemGroups if there's nothing interesting inside of them -> Unreadable code alert. Should do De Morgans magic on this bad boy
          if (
            key.includes('itemGroups') &&
            ((!value.old && (!value.new || value.new === '[]')) ||
              (!value.new && (!value.old || value.old === '[]')))
          ) {
            return false;
          }
          return true;
        }
        return false;
      });
      return hasOtherChanges ? EventType.Modified : undefined;
    },
    [isHistoryDrawerOpen, diff, thisOrderId, rows]
  );

  const virtualItems = rowVirtualizer.getVirtualItems();

  const columnSizeVars = (() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i];
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return colSizes;
  })();

  return (
    <>
      <Flex
        role="table"
        direction="column"
        align="stretch"
        grow="1"
        style={{
          ...columnSizeVars,
          width: table.getTotalSize(),
        }}
      >
        <Flex
          role="row"
          // width={'fit-content'}
          display={'flex'}
          position="sticky"
          top={0}
          zIndex={3}
          color={headerFontColor}
          borderBottom="1px"
          borderColor={borderColor}
          backgroundColor={useColorModeValue('white', 'gray.800')}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <Fragment key={headerGroup.id}>
              {headerGroup.headers.map((header, idx) => {
                return (
                  <AggregatedOrderTableHeader
                    key={header.id}
                    header={header}
                    headerGroup={headerGroup}
                    idx={idx}
                    isRenaming={renamingColumnId !== null}
                  />
                );
              })}
            </Fragment>
          ))}
        </Flex>

        <div
          ref={containerRef}
          style={{
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <Flex
            role="rowgroup"
            direction="column"
            // width={'fit-content'}
            minWidth={'100%'}
            height={rowVirtualizer.getTotalSize()}
          >
            {virtualItems.map((virtualRow, index) => {
              const row = rows[virtualRow.index];
              const eventType = getEventType(row);
              const rowTxtColor = eventType
                ? getTextColor(eventType)
                : undefined;
              const rowBgColor = eventType
                ? getBackgroundColor(eventType)
                : undefined;

              return (
                <Flex
                  role="row"
                  paddingY={0.5}
                  backgroundColor={rowBgColor}
                  color={rowTxtColor}
                  key={virtualRow.key}
                  borderRadius={'lg'}
                  height={`${virtualRow.size}px`}
                  transform={`translateY(${
                    virtualRow.start - index * virtualRow.size
                  }px)`}
                  _hover={{
                    backgroundColor: hoverBackgroundColor,
                    cursor: 'pointer',
                    '.select-cell': {
                      visibility: 'visible !important',
                    },
                  }}
                  onClick={() => {
                    const recursiveSelection = (
                      parent: Row<OrderEntryTableRow>
                    ): Array<[string, boolean]> => {
                      return [
                        [parent.id, true],
                        ...parent.subRows.flatMap((childRow) =>
                          recursiveSelection(childRow)
                        ),
                      ];
                    };
                    const entries = Object.fromEntries(recursiveSelection(row));
                    setRowSelection(entries);
                  }}
                >
                  <TableRow {...{ row }} />
                  {isHistoryDrawerOpen && eventType ? (
                    <Box position="absolute" right="2">
                      <DiffBadge eventType={eventType} />
                    </Box>
                  ) : null}
                </Flex>
              );
            })}
          </Flex>
        </div>
      </Flex>
      <ColumnManagementModal
        isOpen={showColumnManagementModal}
        onClose={onColumnManagementModalClose}
        orderColumns={orderColumns}
        onSubmit={handleColumnManagementSubmit}
        columnNameMapping={columnNameMapping}
      />
    </>
  );
};

const TableRow = memo(
  function TableRow({ row }: { row: Row<OrderEntryTableRow> }) {
    return (
      <>
        {row.getVisibleCells().map((cell) => (
          <Flex
            key={cell.id}
            role="cell"
            padding={1}
            paddingRight={2}
            overflow={
              cell.column.columnDef.id === 'expander' ? 'visible' : 'hidden'
            }
            justifyContent={
              (cell.column.columnDef.meta as { isNumeric: boolean }) &&
              (cell.column.columnDef.meta as { isNumeric: boolean }).isNumeric
                ? 'end'
                : 'start'
            }
            style={{
              width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
              visibility:
                cell.column.columnDef.id === 'select' &&
                !row.getIsSelected() &&
                !row.getIsSomeSelected()
                  ? 'hidden'
                  : 'visible',
              fontWeight: row.getIsSelected() ? 'bold' : 'normal',
            }}
            className={
              cell.column.columnDef.id === 'select' ? 'select-cell' : ''
            }
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </Flex>
        ))}
      </>
    );
  },
  (prev, next) => prev.row.getIsSelected() !== next.row.getIsSelected()
);

export default AggregatedOrderTable;
